/**
 * The usage limit's banner away from the desktop (D372 with D357): "Hand off to…" and "What the backend said", for the
 * console and the phone, DOM-free.
 *
 *  - {@link remoteHandOffChoices}: where the session can go, from what the machine last reported of its runtimes
 *    (`FleetDevice.facts.runtimes`: installed, not switched off), by the workbench's own rule (`buildHandOffOptions`,
 *    protocol/handOff.ts): first the runtime's other named accounts the machine knows are signed in (D414's
 *    `runtimes[].accounts`; a workbench before it reports none, so only other runtimes are offered), then the other
 *    runtimes. The workbench refuses a target that cannot start, in a sentence, and that sentence is shown.
 *  - {@link handOffParams}: `sessions.handOff`'s params (`{ sessionId, target }`, the workbench's Control API).
 *  - {@link backendSaid}: the provider's own message the banner shows on request: the limit event's `detail` (D414,
 *    masked by the workbench), else the stopped status's message that came with it (a workbench before D414).
 *  - {@link limitRanWords}: the model and the account the session ran on, in a line.
 */
import { buildHandOffOptions } from "../protocol/handOff.js";
/**
 * Where the session at its limit can go: the same runtime's other accounts that are signed in (not `current`, the
 * account at its limit, by slug or by name as the limit event names it), then the other runtimes that are installed
 * and switched on.
 */
export function remoteHandOffChoices(provider, runtimes, current) {
    if (!runtimes?.length)
        return [];
    const readiness = Object.fromEntries(runtimes.map((r) => [r.id, { label: r.name, available: r.installed, enabled: r.disabled !== true }]));
    const at = current?.trim();
    const own = provider ? runtimes.find((r) => r.id === provider) : undefined;
    const accounts = own && own.installed && own.disabled !== true ? (own.accounts ?? []).filter((a) => a.signedIn === true && a.slug !== at && a.name !== at).map((a) => ({ runtime: own.id, slug: a.slug, name: a.name })) : [];
    return buildHandOffOptions({ provider, accounts, runtimes: readiness });
}
/** `sessions.handOff`'s params. */
export function handOffParams(sessionId, target) {
    return { sessionId, target };
}
/** The sentence after a hand-off went through. */
export function handedOffWords(option) {
    return `Handed off to ${option.label}. It carries on there for the rest of this session.`;
}
/** The provider's own words, trimmed, when there are some worth showing. */
export function backendSaid(message) {
    const t = message?.trim();
    return t ? t.slice(0, 2000) : undefined;
}
/** "Running claude-opus-4 on the Work account.": what the limit was reached on, when the event says. */
export function limitRanWords(limit) {
    const model = limit.model?.trim();
    const account = limit.account?.trim();
    if (!model && !account)
        return undefined;
    return model ? `Running ${model}${account ? ` on the ${account} account` : ''}.` : `On the ${account} account.`;
}
//# sourceMappingURL=limitHandOff.js.map